import { type ICustomImage } from "@filmweb/sanity-types";
import type { ImageUrlBuilder } from "@sanity/image-url";
import { DEVICE_SIZES, IMAGE_SIZES } from 'src/components/images/imageSizes';
import { urlForAsset } from 'src/lib/client/imageUrls';
import type { ImageHotspot } from 'sanity';

const POSTER_ASPECT = process.env.NEXT_PUBLIC_WEBSITE_BRAND === "kinodk" ? 800 / 1200 : 271 / 384;

const imageSizeRegExp = /^image-.*?-(\d+)x(\d+)-.*$/i;
const imageSizeUrlRegExp = /-(\d+)x(\d+)\./i;
// 9-1920x1080.jpg?auto=format"
type Size = {
    width: number,
    height: number,
    aspect: number
};

export function getImageUrlForWidth(image: ICustomImage, width: number): string {
    const size = getOrgSize(image.asset!._id ?? image.asset!._ref);
    const w = Math.min(width, size.width);
    const h = Math.round(w / size.aspect);
    let imgBuilder = urlForAsset(image.asset!._id ?? image.asset!._ref).auto("format").width(w).height(h);
    if (image.hotspot) {
        imgBuilder = doCrop(imgBuilder, image.asset?.hotspot);
    }
    return imgBuilder.url();
}

export function getOrgSizeForImageUrl(imgUrl?: string): Size | null {
    if( !imgUrl ) return null;
    const res = imageSizeUrlRegExp.exec(imgUrl);
    const w = parseInt(res![1]);
    const h = parseInt(res![2]);
    return { width: w, height: h, aspect: w / h };
}

export function getOrgSize(assetRef: string): Size {

    const res = imageSizeRegExp.exec(assetRef);
    const w = parseInt(res![1]);
    const h = parseInt(res![2]);
    return { width: w, height: h, aspect: w / h };
}

export function doCrop(imageBuilder: ImageUrlBuilder, hotspot?: ImageHotspot): ImageUrlBuilder {
    let fpx = 0.5;
    let fpy = 0.5;
    if (hotspot) {
        fpx = hotspot.x;
        fpy = hotspot.y;
    }
    return imageBuilder.fit("crop").crop("focalpoint").focalPoint(fpx, fpy);
}

export type CmsImageAttrsOptions = {
    assetRef: string;
    width?: number | string;
    height?: number | string;
    sizes?: string;
    unoptimized?: boolean;
    crop?: boolean;
    isPosterAspect?: boolean;
    nonPosterAspect?: number | string;
    hotspot?: ImageHotspot;
    /** Url builder for the Sanity project the image is in. Defaults to the brand's own project. */
    urlFor?: (assetRef: string) => ImageUrlBuilder;
};

export type CmsImageAttrs = {
    src: string;
    srcSet?: string;
    sizes?: string;
    width: number;
    height: number;
};

/**
 * src, srcset, sizes and dimensions for a Sanity image, built the way next/image did with our loader:
 * - unoptimized: a single cropped url for the given size, no srcset
 * - otherwise a srcset of the widths in imageSizes.ts (see getSrcSetWidths)
 */
export function getCmsImageAttrs({ assetRef, width, height, sizes, unoptimized = false, crop = false, isPosterAspect = false, nonPosterAspect, hotspot, urlFor = urlForAsset }: CmsImageAttrsOptions): CmsImageAttrs {
    const size = getOrgSize(assetRef);
    const customAspect = toAspect(nonPosterAspect);
    const aspect = !isPosterAspect && customAspect ? customAspect : size.aspect;
    let w = Number(width ?? size.width);
    let h = Math.round(Number(height ?? w / aspect));
    if (!width && height) {
        w = Math.round(h * aspect);
    }

    if (unoptimized) {
        if (isPosterAspect) {
            h = Math.floor(w / POSTER_ASPECT);
        }
        const src = doCrop(urlFor(assetRef).auto("format").width(w).height(h), hotspot).url();
        return { src, srcSet: undefined, sizes: undefined, width: w, height: h };
    }

    const cropAspect = isPosterAspect ? POSTER_ASPECT : aspect;
    const urlForWidth = (srcWidth: number) => {
        let builder = urlFor(assetRef);
        if (crop) {
            builder = doCrop(builder, hotspot);
        }
        return builder.width(srcWidth).height(Math.floor(srcWidth / cropAspect)).auto("format").url();
    };
    const { widths, kind } = getSrcSetWidths(w, sizes);
    return {
        sizes: !sizes && kind === "w" ? "100vw" : sizes,
        srcSet: widths.map((srcWidth, i) => `${urlForWidth(srcWidth)} ${kind === "w" ? srcWidth : i + 1}${kind}`).join(", "),
        src: urlForWidth(widths[widths.length - 1]),
        width: w,
        height: h,
    };
}

/**
 * The srcset widths next/image picks (getWidths in next/dist/shared/lib/get-img-props.js):
 * with sizes, every size (by w); without, the sizes closest above the width and twice the width (by x).
 */
export function getSrcSetWidths(width: number, sizes?: string): { widths: number[], kind: "w" | "x" } {
    const deviceSizes = [...DEVICE_SIZES].sort((a, b) => a - b);
    const allSizes = [...DEVICE_SIZES, ...IMAGE_SIZES].sort((a, b) => a - b);
    if (sizes) {
        const percentSizes = [...sizes.matchAll(/(^|\s)(1?\d?\d)vw/g)].map(match => parseInt(match[2]));
        if (percentSizes.length) {
            const smallestRatio = Math.min(...percentSizes) * 0.01;
            return { widths: allSizes.filter(s => s >= deviceSizes[0] * smallestRatio), kind: "w" };
        }
        return { widths: allSizes, kind: "w" };
    }
    const widths = [width, width * 2].map(w => allSizes.find(s => s >= w) ?? allSizes[allSizes.length - 1]);
    return { widths: [...new Set(widths)], kind: "x" };
}

function toAspect(aspect?: number | string): number | undefined {
    if (typeof aspect === "string") {
        const [num, den] = aspect.split("/").map(Number);
        return num / den;
    }
    return aspect;
}
